import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { LineChart } from "@mui/x-charts";
import SingleSelect from "./SingleSelect";
import { useEffect, useState } from "react";
import { academicTerms } from "./masterStructure";
import { averageOrNull, numOrNull } from "./marksUtils";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

// Grades displayed in the "By Grade" view, each with its own line color/label.
const GRADES = [
  { key: 8, label: "Grade 8", color: "#FF5733" },
  { key: 7, label: "Grade 7", color: "#000000" },
  { key: 6, label: "Grade 6", color: "#3357FF" },
  { key: 5, label: "Grade 5", color: "#FF33A1" },
  { key: 4, label: "Grade 4", color: "#2ECC71" },
  { key: 3, label: "Grade 3", color: "#95A5A6" },
  { key: 2, label: "Grade 2", color: "#FDDA0D" },
  { key: 1, label: "Grade 1", color: "#A13333" },
  { key: "S", label: "SK", color: "#33A1FF" },
  { key: "J", label: "JK", color: "#7B4397" },
];

// Combines two per-grade mark lists (either side may be absent).
function combineMarks(a, b) {
  return (a || []).concat(b || []);
}

export default function GraphDialog(props) {
  const open = props.open;
  const setOpen = props.setOpen;
  const [filterOptions, changeFilterOptions] = useState({
    gradeAvgSelect: "By Subject",
  });

  function setFilterOptions(event) {
    const { name, value } = event.target;
    changeFilterOptions((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // One entry per academicTerms[i]: { gs: { [grade]: marks[] }, js: { [grade]: marks[] } }.
  // Adding a new school year to masterStructure.js is enough for it to appear here.
  const [marksByTerm, setMarksByTerm] = useState([]);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setMarksByTerm(
      academicTerms.map((term) => {
        const gs = {};
        const js = {};

        // A row can be missing its class assignment (e.g. a new/incomplete
        // registration) — skip it rather than letting the whole computation throw.
        for (const student of props.filteredData) {
          const classCode = student[5];
          if (!classCode) continue;

          const grade = classCode[0];
          const gsMark = numOrNull(student[term.gsIndex]);
          const jsMark = numOrNull(student[term.jsIndex]);

          if (gsMark !== null) {
            gs[grade] = (gs[grade] || []).concat(gsMark);
          }

          if (jsMark !== null) {
            js[grade] = (js[grade] || []).concat(jsMark);
          }
        }

        return { gs, js };
      }),
    );
  }, [props.filteredData]);

  const bySubjectSeries = () => [
    {
      data: marksByTerm.map((term) =>
        averageOrNull(Object.values(term.gs).flat()),
      ),
      label: "General Studies",
    },
    {
      data: marksByTerm.map((term) =>
        averageOrNull(Object.values(term.js).flat()),
      ),
      label: "Jewish Studies",
    },
  ];

  const byGradeSeries = () =>
    GRADES.map(({ key, label, color }) => ({
      data: marksByTerm.map((term) =>
        averageOrNull(combineMarks(term.gs[key], term.js[key])),
      ),
      label,
      color,
    }));

  const series =
    filterOptions.gradeAvgSelect === "By Subject"
      ? bySubjectSeries()
      : byGradeSeries();

  const dataPoints = series
    .flatMap((s) => s.data)
    .filter((val) => val !== null);

  const calculateYAxisRange = () => {
    if (dataPoints.length === 0) {
      return { min: 0, max: 4 }; // Default range if no data
    }

    const minValue = Math.min(...dataPoints);
    const maxValue = Math.max(...dataPoints);

    // Add some padding (10% of the range) to ensure data points aren't right at the edges
    const range = maxValue - minValue;
    const padding = Math.max(0.2, range * 0.1); // Minimum padding of 0.2

    return {
      min: Math.max(0, minValue - padding), // Don't go below 0
      max: Math.min(4, maxValue + padding), // Don't go above 4 (assuming 4 is max grade)
    };
  };

  const yAxisRange = calculateYAxisRange();

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Filtered Student Grades
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <SingleSelect
            filterOptions={filterOptions}
            setFilterOptions={setFilterOptions}
            options={["By Subject", "By Grade"]}
            labelId={"gradeAvgSelectLabel"}
            compId={"gradeAvgSelect"}
            compLabel={"View"}
            compName={"gradeAvgSelect"}
          />
          <LineChart
            yAxis={[{ min: yAxisRange.min, max: yAxisRange.max }]}
            xAxis={[
              {
                scaleType: "point",
                data: academicTerms.map(
                  (term) => `T${term.termNumber} ${term.year}`,
                ),
              },
            ]}
            series={series}
            height={500}
          />
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}
